/**
 * ErrorState — top-of-content banner shown when a query fails or a
 * mutation errors. Always pairs a human-readable title + body with a
 * retry control.
 *
 * Optional `error` prop: when passed *and* we are running in dev mode
 * (`import.meta.env.DEV`) *and* the error is an `ApiHttpError`, a
 * collapsed disclosure surfaces the RFC 7807 `status`, `code`, and
 * `detail` fields so engineers can diagnose without opening devtools.
 * Ignored in production builds, so customer-facing UI never leaks
 * error internals.
 */

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { ApiHttpError } from '@/api/http';

export function ErrorState({
  title,
  body,
  onRetry,
  actions,
  error,
}: {
  title: ReactNode;
  body: ReactNode;
  onRetry?: () => void;
  actions?: ReactNode;
  /** Optional underlying error. If it is an `ApiHttpError` and the
   *  bundle was built in dev mode, a collapsed 7807 detail panel is
   *  rendered below the body. */
  error?: unknown;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const showDevPanel = import.meta.env.DEV && error instanceof ApiHttpError;

  return (
    <div
      role="alert"
      className="flex flex-col items-start gap-3 rounded-lg border border-danger/30 bg-surface p-5"
    >
      <div className="w-full">
        <h2 className="text-sm font-semibold text-danger">{title}</h2>
        <p className="mt-1 text-sm text-ink-muted">{body}</p>

        {showDevPanel ? (
          <DevProblemPanel
            error={error as ApiHttpError}
            expanded={expanded}
            onToggle={() => setExpanded((x) => !x)}
          />
        ) : null}
      </div>
      <div className="flex gap-2">
        {onRetry ? (
          <Button variant="secondary" size="sm" onClick={onRetry}>
            {t('actions.retry')}
          </Button>
        ) : null}
        {actions}
      </div>
    </div>
  );
}

function DevProblemPanel({
  error,
  expanded,
  onToggle,
}: {
  error: ApiHttpError;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="mt-3 rounded border border-dashed border-border bg-canvas">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-center justify-between px-3 py-2 text-start text-xs font-mono text-ink-muted hover:text-ink"
      >
        <span>
          {error.status} · {error.code}
        </span>
        <span className="font-sans text-ink-subtle">
          {expanded ? t('actions.close') : t('errors.dev_show_details')}
        </span>
      </button>
      {expanded ? (
        <dl className="border-t border-border px-3 py-2 text-xs font-mono text-ink">
          <div className="flex gap-2 py-1">
            <dt className="w-16 text-ink-subtle">status</dt>
            <dd>{error.status}</dd>
          </div>
          <div className="flex gap-2 py-1">
            <dt className="w-16 text-ink-subtle">code</dt>
            <dd>{error.code}</dd>
          </div>
          <div className="flex gap-2 py-1">
            <dt className="w-16 text-ink-subtle">title</dt>
            <dd className="flex-1 break-words">{error.title}</dd>
          </div>
          {error.detail ? (
            <div className="flex gap-2 py-1">
              <dt className="w-16 text-ink-subtle">detail</dt>
              <dd className="flex-1 break-words">{error.detail}</dd>
            </div>
          ) : null}
        </dl>
      ) : null}
    </div>
  );
}
